import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';

import { dark, light, radius } from './tokens';

/**
 * Glassmorphism, in one dialect: anything that wants to look like glass comes
 * through here. SiteHeader still frosts itself inline (`saturate(180%)
 * blur(12px)`), because its surface animates from transparent to frosted as
 * the visitor scrolls rather than simply being glass; fold it in here if that
 * ever changes.
 *
 * Four layers make a pane, and a tint only supplies the values for each:
 *
 * 1. `backdrop` — what happens to the band on the way through the pane. This
 *    is the layer that decides whether the thing reads as glass at all. It
 *    always *saturates*: pulling the backdrop's colour through brighter is
 *    what separates frosted glass from frosted plastic, and a pane that
 *    desaturates what it covers has no way back to looking like glass.
 * 2. `fill` + `sheen` — a thin wash of the tint, and a specular sweep off the
 *    top-left corner painted over it. The sweep is on the element's own
 *    `backgroundImage` rather than a pseudo-element, so it composites onto
 *    the fill directly and needs no stacking games.
 * 3. `ring` — the rim, and the second-biggest tell. Real glass catches the
 *    light on the edges facing it and loses it on the edges that don't, so
 *    the rim is a gradient and not a stroke; a rim at one even alpha all the
 *    way round reads as a box someone drew a border on.
 * 4. `shadow` — an inset lip and a real ambient drop, so the pane sits above
 *    the band instead of being printed on it.
 *
 * Glass is never load-bearing for contrast. Every rule below has a flat
 * counterpart for the two cases where the blur is not there — a browser
 * without `backdrop-filter`, and a visitor who has asked for less
 * transparency — and the text colours are chosen to clear 4.5:1 against the
 * *opaque* fill, so the panel is legible in all three states (§50).
 *
 * `dark`: a smoked panel that ignores the colour scheme. For the hero, which
 * is #070708 in both light and dark mode — a scheme-following panel turns
 * into a bright slab over it.
 * `theme`: follows the colour scheme. For glass on an ordinary page band.
 * `clear`: no fill to speak of. For a panel whose job is to let the band
 * behind it through: the backdrop keeps its colour and its shape, and the
 * pane only frosts and dims it. Needs a band with something *in* it —
 * over a flat colour there is nothing for the frost to refract and it comes
 * out a grey slab, which is the failure this tint exists to avoid.
 */
export type GlassTint = 'clear' | 'dark' | 'theme';

/**
 * The house blur.
 *
 * Short of the 30px+ that stops reading as glass and starts reading as smear,
 * and saturated well past 100% — see the note on `backdrop` above. Every tint
 * starts from this and only says what it changes.
 */
const BLUR = 'blur(22px) saturate(180%)';

/**
 * `clear`'s backdrop.
 *
 * Frosted glass does not darken what it covers — it *hazes* it. It lifts the
 * blacks, pulls the highlights down and leaves everything between them closer
 * together than it was, which is why a real pane reads as a soft, even field
 * that still has the shape and the colour of the thing behind it. That is the
 * effect this recipe is aiming at, and it is produced by the fill and the
 * blur together, not by `brightness()`.
 *
 * The band behind this panel was measured rather than guessed: over the
 * import page's opening band, with a car passing behind the pane, the raw
 * backdrop runs L=0.026 at the 25th percentile to L=0.119 at the 99th. Put
 * through this filter and the 0.14 fill, those become L=0.081 and L=0.167 —
 * the dark end three times lighter, the bright end barely touched. The pane
 * lifts the band; it does not replace it.
 *
 * `brightness(0.80)` is a *ceiling*, not the effect. It exists so a specular
 * highlight on a passing car cannot push the interior past the point where
 * white text drops under 4.5:1 (WCAG 1.4.3), which on this band is around
 * L=0.183. At the measured 99th percentile, white lands at 4.8:1 and the
 * accent icons at 3.2:1, past the 3:1 non-text floor (1.4.11).
 *
 * An earlier pass had this at 0.42, which is where the pane stopped being
 * glass: on a band already this dark, dimming it further leaves nothing for
 * the frost to show and the panel reads as an outlined rectangle. If this
 * ever looks wrong, change the fill first — the brightness is the guardrail.
 */
const CLEAR_BACKDROP = 'blur(26px) saturate(190%) brightness(0.80)';

const NO_BACKDROP =
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))';
const REDUCED_TRANSPARENCY = '@media (prefers-reduced-transparency: reduce)';
/** Where the gradient rim cannot be drawn and a flat one stands in for it. */
const NO_MASK = '@supports not ((mask-composite: exclude) or (-webkit-mask-composite: xor))';

/** The fields' border, which is now the whole field. */
const FIELD_RIM = 'rgba(255,255,255,0.30)';
const FIELD_RIM_HOVER = 'rgba(255,255,255,0.55)';

/** The label band above a field on glass; the submit button clears the same height. */
const LABEL_FONT_SIZE = '0.8125rem';
const LABEL_LINE_HEIGHT = 1.4;
const LABEL_GAP = '6px';

type Recipe = {
  /** Overrides BLUR where the fill is too thin to carry the separation. */
  backdrop?: string;
  fill: string;
  /** The specular sweep, painted over the fill: one light source off the top-left. */
  sheen: string;
  /** Used wherever the blur is absent, so the fill alone carries the contrast. */
  flatFill: string;
  /** The graded rim. Brightest where the light lands, all but gone opposite it. */
  ring: string;
  /** The same rim at one alpha, for a browser that cannot draw the gradient one. */
  rim: string;
  /** Inset bottom lip, then the ambient drop. */
  shadow: string;
};

/**
 * The rim's path round the pane.
 *
 * 145deg, matching the sheen's light source: the top and left edges face the
 * light and take most of it, the middle of the run falls away, and the
 * bottom-right lifts again — an edge lit from one side still catches a little
 * on the far side, and without that return the pane looks like it was cut off
 * rather than closed.
 *
 * The trough never goes to nothing. A rim that fades out completely leaves the
 * pane with two edges and two gaps, which reads as a broken box rather than as
 * glass turned away from the light; 0.7 of the shade value is the floor that
 * keeps the whole outline closed while still being plainly darker than the lit
 * edge. Each tint's trough is set at roughly the flat rim it replaced, so the
 * gradient only ever adds an edge and never takes one away.
 *
 * White only, so this is for the two tints that sit on something dark. A pane
 * on a light surface takes its unlit edges from the ink instead — see the
 * `theme` recipe, which cannot use this.
 */
const ring = (lit: number, shade: number, bounce: number) =>
  `linear-gradient(145deg, rgba(255,255,255,${lit}) 0%, rgba(255,255,255,${shade}) 34%, rgba(255,255,255,${(shade * 0.7).toFixed(3)}) 60%, rgba(255,255,255,${bounce}) 100%)`;

/**
 * The sheen's stops.
 *
 * Three of them, not two: a linear fade from lit to nothing spreads the
 * highlight over the whole pane and reads as a tint. Front-loading it — most
 * of the light gone by a quarter of the way across — keeps it a highlight on
 * the corner the light comes from.
 */
const sheen = (lit: number, mid: number) =>
  `linear-gradient(145deg, rgba(255,255,255,${lit}) 0%, rgba(255,255,255,${mid}) 24%, rgba(255,255,255,0) 52%)`;

const recipes: Record<GlassTint, Recipe> = {
  clear: {
    backdrop: CLEAR_BACKDROP,
    // The haze. Heavier than a tint, and heavier than the fill this recipe
    // started with, because on a band this dark a 0.06 wash is invisible and
    // the pane falls back to being a rim round some text. This is the layer
    // that lifts the blacks — see CLEAR_BACKDROP for the measured numbers.
    //
    // It still is not a *surface*: at 0.14 the car behind it is plainly
    // legible through the frost, which is the test. Past ~0.20 it stops being
    // and starts being a pale slab, which is the thing this tint exists not
    // to be.
    fill: 'rgba(255,255,255,0.14)',
    sheen: sheen(0.16, 0.05),
    // The one place this recipe is not clear. With no blur there is no frost
    // and no dimming either, so a 0.06 fill would leave white content sitting
    // directly on the band in its full brightness; the flat state trades the
    // look for the reading, as every tint here does. Neutral, and close to
    // what the dimmed backdrop lands on, so the two states agree.
    flatFill: 'rgba(20,20,22,0.80)',
    // The brightest rim of the three. With a fill this thin the rim is most
    // of what draws the pane's edge, and it is drawing it against a backdrop
    // that is only a little darker than the pane itself.
    ring: ring(0.60, 0.26, 0.36),
    rim: 'rgba(255,255,255,0.26)',
    shadow: [
      'inset 0 -1px 0 rgba(255,255,255,0.06)',
      // Softer and shallower than `dark`'s. A panel you can see through does
      // not sit as heavily as a smoked one, and a deep shadow under clear
      // glass reads as a mistake.
      '0 18px 40px -16px rgba(0,0,0,0.42)',
      '0 4px 12px -6px rgba(0,0,0,0.26)',
    ].join(','),
  },
  dark: {
    fill: 'rgba(18,18,21,0.52)',
    sheen: sheen(0.13, 0.04),
    flatFill: 'rgba(18,18,21,0.97)',
    ring: ring(0.45, 0.22, 0.28),
    rim: 'rgba(255,255,255,0.16)',
    shadow: [
      'inset 0 -1px 0 rgba(255,255,255,0.05)',
      '0 28px 56px -20px rgba(0,0,0,0.78)',
      '0 6px 16px -8px rgba(0,0,0,0.45)',
    ].join(','),
  },
  theme: {
    fill: 'rgba(var(--mui-palette-background-paperChannel) / 0.66)',
    sheen: sheen(0.4, 0.1),
    flatFill: 'rgba(var(--mui-palette-background-paperChannel) / 0.98)',
    // The one rim that is not white all the way round, because this is the one
    // pane that sits on a light surface. A white edge on a near-white page is
    // no edge at all: run the `ring()` helper here and the card loses its top
    // and left sides entirely and keeps only a faint bottom-right.
    //
    // So the lit corner is white and the rest of the run is ink — an edge
    // turned away from the light on a light ground is a *shadow*, not a
    // fainter highlight. The three ink stops pass through the old flat
    // `divider` on the way, so no side is weaker than it used to be.
    ring: [
      'linear-gradient(145deg,',
      'rgba(255,255,255,0.85) 0%,',
      'rgba(var(--mui-palette-text-primaryChannel) / 0.05) 30%,',
      'rgba(var(--mui-palette-text-primaryChannel) / 0.13) 60%,',
      'rgba(var(--mui-palette-text-primaryChannel) / 0.20) 100%)',
    ].join(' '),
    rim: 'var(--mui-palette-divider)',
    shadow: ['0 22px 44px -20px rgba(11,11,12,0.30)', '0 4px 12px -6px rgba(11,11,12,0.16)'].join(
      ',',
    ),
  },
};

/**
 * The `theme` tint's light values are for a white card. Its fill follows the
 * colour scheme on its own, through `paperChannel`, but the highlight, the rim
 * and the shadow cannot: a 0.7-white rim and a 0.4-white sweep read as a smudge
 * across a dark card. Only the lighting changes here, never the recipe.
 */
const themeTintInDark: Pick<Recipe, 'shadow' | 'sheen' | 'ring'> = {
  shadow: ['0 22px 44px -20px rgba(0,0,0,0.72)', '0 4px 12px -6px rgba(0,0,0,0.45)'].join(','),
  sheen: sheen(0.1, 0.03),
  // A dark card on a dark page is back to a white rim: here the *lit* edge is
  // the visible one and the ink stops of the light ring would disappear.
  ring: ring(0.30, 0.13, 0.19),
};

/** One entry of an `sx` array — what this module hands back to be spread. */
type GlassSx = Exclude<SxProps<Theme>, readonly unknown[]>;

/**
 * Paints `ring` as a 1px gradient border.
 *
 * A `border` cannot take a gradient, so the rim is a filled rectangle the size
 * of the pane with its own middle masked out: two full-box masks, one clipped
 * to the content box, composited with `exclude` so only the 1px of padding
 * between them survives. It sits on `::after` and therefore over the content,
 * which is where a rim belongs and costs nothing — it is a hairline at the
 * very edge with no pointer events.
 *
 * `inset: 0` resolves against the padding box, so the surface carries no
 * `border` of its own; the ring *is* the border. Where the mask cannot be
 * composited, NO_MASK below drops this and puts the flat rim back as an
 * `outline` pulled inside the box, which leaves the layout alone the way a
 * real border would not.
 */
const RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)';

/**
 * The frosted surface itself: backdrop, fill, sheen, rim, shadow.
 *
 * Returned as `sx` entries rather than one object, so the dark-scheme
 * adjustment can come from `theme.applyStyles` — which knows the selector the
 * theme actually emits — without its `CSSObject` being spread into a style
 * literal, where it would give the literal an index signature and stop every
 * other property in it type-checking as itself. Spread it, don't nest it:
 * `sx={[...glassSurfaceSx(tint), ...yours]}`.
 */
export const glassSurfaceSx = (tint: GlassTint): GlassSx[] => {
  const recipe = recipes[tint];
  const backdrop = recipe.backdrop ?? BLUR;
  const surface: GlassSx = {
    position: 'relative',
    isolation: 'isolate',
    borderRadius: `${radius.xl}px`,
    backgroundColor: recipe.fill,
    backgroundImage: recipe.sheen,
    backdropFilter: backdrop,
    WebkitBackdropFilter: backdrop,
    boxShadow: recipe.shadow,
    // Both scheme-ignoring tints put content on something dark — `dark` on its
    // own fill, `clear` on the band it dims on the way through.
    ...(tint !== 'theme' && { color: '#FFFFFF' }),
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '1px',
      background: recipe.ring,
      WebkitMask: RING_MASK,
      mask: RING_MASK,
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    [NO_MASK]: {
      '&::after': { display: 'none' },
      outline: `1px solid ${recipe.rim}`,
      outlineOffset: '-1px',
    },
    // Without the blur there is no frost to catch the sheen, and a white
    // gradient over an opaque fill is just a smudge across the top corner.
    [NO_BACKDROP]: { backgroundColor: recipe.flatFill, backgroundImage: 'none' },
    [REDUCED_TRANSPARENCY]: {
      backgroundColor: recipe.flatFill,
      backgroundImage: 'none',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
  };

  if (tint !== 'theme') return [surface];
  return [
    surface,
    // `applyStyles` is typed as a bare CSSObject; as an sx entry it is a
    // style object for this theme.
    (theme) =>
      theme.applyStyles('dark', {
        backgroundImage: themeTintInDark.sheen,
        boxShadow: themeTintInDark.shadow,
        '&::after': { background: themeTintInDark.ring },
      }) as SystemStyleObject<Theme>,
  ];
};

/**
 * A form sitting directly on a `dark` glass panel.
 *
 * The panel is frosted; the fields are not. A translucent field over
 * translucent glass leaves the chosen value competing with whatever is two
 * layers down, and the native `<option>` list is painted by the OS, where our
 * styling cannot follow it.
 */
export const darkGlassFormSx: SxProps<Theme> = {
  // Every label now occupies a band above its field, so anything in the row
  // that has no label — the submit button — has to come down by the same
  // amount to stay on the fields' line. Stated once, in the label's own terms.
  '--kabex-field-label-offset': `calc(${LABEL_FONT_SIZE} * ${LABEL_LINE_HEIGHT} + ${LABEL_GAP})`,

  // Labels leave the border notch and sit above the field. Notched, a label
  // crosses from the glass onto a white fill mid-glyph and is legible on
  // neither; above it, one colour does the whole word (§39).
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    maxWidth: '100%',
    marginBottom: LABEL_GAP,
    fontSize: LABEL_FONT_SIZE,
    lineHeight: LABEL_LINE_HEIGHT,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.80)',
    '&.Mui-focused': { color: '#FFFFFF' },
    // A disabled control is exempt from the contrast minimum, but its label
    // still has to be readable enough to explain why the field is inert.
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.58)' },
  },
  // Only where the button shares a line with a field. Stacked on a phone it
  // sits under them and has nothing to clear.
  '& button[type="submit"]': {
    marginTop: { xs: 0, sm: 'var(--kabex-field-label-offset)' },
    // The primary action joins the glass rather than sitting on top of it as
    // the one solid object in the panel: still unmistakably the brand red,
    // but tinted glass with the same rim as the fields.
    backgroundColor: alpha(light.primary, 0.78),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${FIELD_RIM_HOVER}`,
    color: '#FFFFFF',
    '&:hover': { backgroundColor: alpha(light.primary, 0.94) },
    // Opaque wherever the blur is absent, so the label keeps its 5:1.
    [NO_BACKDROP]: { backgroundColor: light.primary },
    [REDUCED_TRANSPARENCY]: { backgroundColor: light.primary, backdropFilter: 'none' },
  },
  // With the label out of the notch, the top border has no gap to leave.
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },

  // The field is its border and nothing else: no fill, so the panel's own
  // frost carries straight through it and the value sits in white on glass.
  '& .MuiOutlinedInput-root': {
    borderRadius: `${radius.md}px`,
    backgroundColor: 'transparent',
    color: '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_RIM },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_RIM_HOVER },
    // A focus ring in the panel's own light: the theme's red outline alone is
    // hard to pick out against a rim that is already close to it in weight.
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(255,255,255,0.22)' },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.12)',
    },
  },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,0.45)' },

  // The closed select is white on glass; its dropdown is drawn by the OS, so
  // that list is given both colours of its own — a dark system theme would
  // otherwise put white option text on a white popup.
  '& select': { backgroundColor: 'transparent', color: '#FFFFFF' },
  '& option': { color: light.ink, backgroundColor: '#FFFFFF' },
  '& .MuiSelect-icon, & .MuiNativeSelect-icon': { color: 'rgba(255,255,255,0.75)' },

  '& .MuiFormHelperText-root': {
    ml: 0,
    color: 'rgba(255,255,255,0.72)',
    '&.Mui-error': { color: dark.error },
  },

  // "More filters" is a ghost button, i.e. ink-coloured, which disappears here.
  '& .MuiButton-text': {
    color: '#FFFFFF',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
  },
};
